/**
 * components/RightTabStrip.tsx —— **右栏 tab 条**（胶囊 + 末端四控件）。
 *
 * 真源：`102:frontend_src/src/components/RightTabStrip.vue`（235 行）——**抄形态/尺寸/交互，译 Vue→React+TS**。
 * 依据：《UI 全局布局壳规范》v1.2 §5（**无标题行**；形态切换·折叠两控件搭在 **tab 条末端**）；施工方案 §4 U-B2。
 *
 * 契约（逐条承源件批 7-A P1 §10.2.1）：
 *  · 键盘完备**五路＋Delete**：←/↑ 前移、→/↓ 后移（环绕）、Home/End 首尾、`Delete` 关当前 tab；
 *  · **roving tabindex**：仅活跃 tab `tabIndex=0`，其余 −1；tab 内**禁嵌套可聚焦元素**（关闭钮 tabIndex=−1）；
 *  · 🔴 与源件差异：**React 禁 button 嵌 button**（源件外层是 button）⇒ 外层改 `div role="tab"`
 *    （a11y 语义不变；任务书写死此形态）；Enter/Space 经 keydown 激活（div 无原生激活）；
 *  · 兜底页 kind：`FALLBACK_TAB_KIND = "progress"`（源件 102 `rightbar/tabRegistry.js:51`；
 *    e2e 按 `--progress` 类筛「非兜底页 tab」⇒ **判别钩子类名保留**，不斜体，字色 tertiary）；
 *  · 末端控件四枚＝新建标签/分栏/切换形态/折叠（源件 `controls` 段逐一对应）。
 *
 * 纪律：本件**不渲染 tab 正文**（源件禁做项照抄）；色/间距/字号一律 `var(--xg-*)`；
 *      **禁 hover-only**（hover 包 `@media (hover:hover)`）；零取数，数据只由 props 进。
 */
import React, { useRef } from "react";

/** 兜底页 tab kind（源件单一常量源 `FALLBACK_TAB_KIND='progress'`；102 tabRegistry.js:51 逐字） */
import { FALLBACK_TAB_KIND } from "./rightbar/tabRegistry";

/** 兜底页 tab kind —— **单一常量源＝注册表**（本件只转发，禁再写一份字面量） */
export { FALLBACK_TAB_KIND };

export interface RightTabRecord {
  tabId: string;
  tabKind: string;
  title?: string;
  /** 缺省可关（源件 `closable !== false` 判定） */
  closable?: boolean;
}

export interface RightTabStripProps {
  /** tab 记录列表（只读数组） */
  tabs?: RightTabRecord[];
  /** 当前活跃 tab id */
  activeTabId?: string | null;
  onFocusTab?: (tabId: string) => void;
  onCloseTab?: (tabId: string) => void;
  onNewTab?: () => void;
  onSplitPane?: () => void;
  onTogglePresentation?: () => void;
  onToggleCollapse?: () => void;
}

/** 界面图标（静态内联 SVG，承源件 UiIcon 形制：outline／currentColor／stroke-width 1.5；
 *  本任务工作面禁建 components/icons/ ⇒ 五枚所需形制内联于本件，路径逐字照抄 UiIcon.vue） */
const ICON_PATHS: Record<string, { box: number; d: string[] }> = {
  close: { box: 12, d: ["M3 3l6 6M9 3l-6 6"] },
  "tab-new": { box: 14, d: ["M7 2v10M2 7h10"] },
  split: { box: 14, d: ["M2 1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Z", "M7 1v12"] },
  presentation: {
    box: 14,
    d: ["M2 1h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Z",
        "M9 1h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Z"],
  },
  collapse: { box: 14, d: ["M10 3l-4 4 4 4"] },
};

function UiIcon({ name, size }: { name: string; size?: number }): React.ReactElement {
  const def = ICON_PATHS[name] ?? ICON_PATHS["tab-new"]; // 未登记名 ⇒ 回退（不崩、不空白，承源件）
  const side = size ?? def.box;
  return (
    <svg className="xg-right-tab-strip__icon" width={side} height={side} viewBox={`0 0 ${def.box} ${def.box}`} fill="none" aria-hidden="true">
      {def.d.map((p, i) => (
        <path key={i} d={p} stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

/** tab 条（role=tablist） */
export function RightTabStrip(props: RightTabStripProps): React.ReactElement {
  const tabs = props.tabs ?? [];
  const { activeTabId } = props;
  const refs = useRef(new Map<string, HTMLDivElement>());

  const focusTabAt = (index: number): void => {
    const target = tabs[index];
    if (!target) return;
    props.onFocusTab?.(target.tabId); // 源件 focusTabAt＝移动**并聚焦**：先上报（宿主置 active），后聚焦
    refs.current.get(target.tabId)?.focus();
  };

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, i: number): void => {
    if (!tabs.length) return;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown": e.preventDefault(); focusTabAt((i + 1) % tabs.length); break;
      case "ArrowLeft":
      case "ArrowUp": e.preventDefault(); focusTabAt((i - 1 + tabs.length) % tabs.length); break;
      case "Home": e.preventDefault(); focusTabAt(0); break;
      case "End": e.preventDefault(); focusTabAt(tabs.length - 1); break;
      case "Delete": e.preventDefault(); props.onCloseTab?.(tabs[i].tabId); break;
      case "Enter":
      case " ": e.preventDefault(); props.onFocusTab?.(tabs[i].tabId); break; // div role=tab 无原生激活 ⇒ 键激活
      default: break;
    }
  };

  return (
    <div className="xg-right-tab-strip" data-testid="right-tab-strip" role="tablist" aria-label="停靠面标签">
      <div className="xg-right-tab-strip__tabs">
        {tabs.map((tab, i) => {
          const active = tab.tabId === activeTabId;
          return (
            <div
              key={tab.tabId}
              ref={(el) => { if (el) refs.current.set(tab.tabId, el); else refs.current.delete(tab.tabId); }}
              role="tab"
              data-testid={"tab-" + tab.tabId}
              className={"xg-right-tab-strip__tab"
                + (active ? " xg-right-tab-strip__tab--active" : "")
                + (tab.tabKind === FALLBACK_TAB_KIND ? " xg-right-tab-strip__tab--progress" : "")}
              tabIndex={active ? 0 : -1}
              aria-selected={active}
              aria-label={tab.title || tab.tabKind}
              onClick={() => props.onFocusTab?.(tab.tabId)}
              onKeyDown={(e) => onTabKeyDown(e, i)}
            >
              <span className="xg-right-tab-strip__tab-label">{tab.title || tab.tabKind}</span>
              {tab.closable !== false ? (
                <button
                  type="button"
                  className="xg-right-tab-strip__tab-close"
                  data-testid={"tab-close-" + tab.tabId}
                  tabIndex={-1}
                  aria-label={`关闭 ${tab.title || tab.tabKind}`}
                  onClick={(e) => { e.stopPropagation(); props.onCloseTab?.(tab.tabId); }}
                >
                  <UiIcon name="close" size={12} />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="xg-right-tab-strip__controls">
        <button type="button" className="xg-right-tab-strip__ctrl" data-testid="tab-ctrl-new" aria-label="新建标签" aria-haspopup="menu" onClick={() => props.onNewTab?.()}>
          <UiIcon name="tab-new" size={14} />
        </button>
        <button type="button" className="xg-right-tab-strip__ctrl" data-testid="tab-ctrl-split" aria-label="分栏" onClick={() => props.onSplitPane?.()}>
          <UiIcon name="split" size={14} />
        </button>
        <button type="button" className="xg-right-tab-strip__ctrl" data-testid="tab-ctrl-presentation" aria-label="切换形态" onClick={() => props.onTogglePresentation?.()}>
          <UiIcon name="presentation" size={14} />
        </button>
        <button type="button" className="xg-right-tab-strip__ctrl" data-testid="tab-ctrl-collapse" aria-label="折叠停靠面" onClick={() => props.onToggleCollapse?.()}>
          <UiIcon name="collapse" size={14} />
        </button>
      </div>

      <style>{STYLE}</style>
    </div>
  );
}

/** 自包含样式（源件 `<style scoped>` 译入；真名 token；`xg-right-tab-strip` 前缀隔离。
 *  命中区：源件 `.xg-touch-44`（coarse 指针媒体查询）⇒ 新树控件基线恒 44（shell.css `.xg-btn` 同口径），
 *  本件 ctrl 观感 24、命中区经 `::after` 撑至 44（视觉可小、命中区须 ≥44，§1bis·三） */
import { rightTabStripStyle as STYLE } from "../styles/inlineStyles";

export default RightTabStrip;
